import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, Plus, Loader2, Cpu, Shield, Zap } from "lucide-react";
import {
  useSentinelRules, useToggleRule, useCreateRule,
  useSentinelModelRegistry, useUpdateModelStatus,
  useRunSentinelAgent,
} from "@/hooks/useSentinelCycle";
import {
  AGENTS, METRIC_OPTIONS, CONDITION_OPTIONS, MODEL_STATUS_COLORS, SEVERITY_CONFIG,
  type Severity,
} from "./sentinel-constants";
import { SentinelCycleRunner } from "./SentinelCycleRunner";
import { AgentResultDrawer, type AgentRunResult } from "./AgentResultDrawer";

interface Props {
  orgId?: string;
}

export default function SentinelConfig({ orgId }: Props) {
  const { data: rules = [], isLoading: lr } = useSentinelRules();
  const { data: models = [], isLoading: lm } = useSentinelModelRegistry();
  const toggleRule = useToggleRule();
  const createRule = useCreateRule();
  const updateModel = useUpdateModelStatus();
  const runAgent = useRunSentinelAgent();

  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [agentResult, setAgentResult] = useState<AgentRunResult | null>(null);

  const handleRunAgent = async (agentId: string) => {
    const agent = AGENTS.find((a) => a.id === agentId);
    if (!agent) return;
    setAgentResult({ agent, status: "running", startedAt: new Date().toISOString() });
    setDrawerOpen(true);
    try {
      const result = await runAgent.mutateAsync({ agentId, org_id: orgId });
      setAgentResult({
        agent: result.agent,
        status: "success",
        data: result.data,
        startedAt: new Date().toISOString(),
      });
    } catch (err) {
      setAgentResult({
        agent,
        status: "error",
        error: err instanceof Error ? err.message : String(err),
        startedAt: new Date().toISOString(),
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Cycle Runner */}
      <SentinelCycleRunner orgId={orgId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Rules */}
        <Card>
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" aria-hidden="true" />
              Regras
              <Badge variant="outline" className="ml-2">{rules.length}</Badge>
            </CardTitle>
            <CreateRuleDialog
              open={createOpen}
              onOpenChange={setCreateOpen}
              orgId={orgId}
              onCreate={(payload) => createRule.mutate(payload, { onSuccess: () => setCreateOpen(false) })}
              isPending={createRule.isPending}
            />
          </CardHeader>
          <CardContent className="space-y-2">
            {lr ? (
              <Skeleton className="h-24" />
            ) : rules.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                Nenhuma regra configurada. Clique em + para criar.
              </div>
            ) : (
              rules.map((r) => {
                const sev = (r.severity as Severity) ?? "low";
                const sc = SEVERITY_CONFIG[sev];
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 p-2 rounded border border-border hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-foreground truncate">{r.name}</span>
                        <Badge variant={sc.badge} className="text-[10px]">{sc.label}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {r.metric} {r.condition} {r.threshold} · cooldown {r.cooldown_minutes}min
                      </p>
                    </div>
                    <Switch
                      checked={r.active}
                      onCheckedChange={(checked) => toggleRule.mutate({ id: r.id, active: checked })}
                      aria-label={`Ativar regra ${r.name}`}
                    />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Model Registry */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Cpu className="w-4 h-4 text-primary" aria-hidden="true" />
              Model Registry
              <Badge variant="outline" className="ml-2">{models.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {lm ? (
              <Skeleton className="h-24" />
            ) : models.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                Nenhum modelo registrado. Execute uma auditoria para popular.
              </div>
            ) : (
              models.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between gap-2 p-2 rounded border border-border hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-foreground truncate">
                        {m.provider}/{m.model_name}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      ${Number(m.cost_per_1k_input).toFixed(4)} in / ${Number(m.cost_per_1k_output).toFixed(4)} out
                      {m.latency_avg_ms ? ` · ${m.latency_avg_ms}ms` : ""}
                    </p>
                  </div>
                  <Select
                    value={m.status}
                    onValueChange={(status) => updateModel.mutate({ id: m.id, status })}
                  >
                    <SelectTrigger className="w-32 h-7 text-xs">
                      <SelectValue asChild>
                        <span>
                          <Badge variant={MODEL_STATUS_COLORS[m.status] || "outline"} className="text-[10px]">
                            {m.status}
                          </Badge>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">approved</SelectItem>
                      <SelectItem value="experimental">experimental</SelectItem>
                      <SelectItem value="deprecated">deprecated</SelectItem>
                      <SelectItem value="blocked">blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agents Grid */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" aria-hidden="true" />
            Agentes Sentinel
            <Badge variant="outline" className="ml-2">{AGENTS.length}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Execute agentes individualmente para inspecionar resultado detalhado.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {AGENTS.map((a) => {
              const Icon = a.icon;
              const isPending = runAgent.isPending && runAgent.variables?.agentId === a.id;
              const disabled = isPending || !orgId || (a.needs === "audit_id" || a.needs === "alert_id" || a.needs === "incident_id");
              return (
                <button
                  key={a.id}
                  onClick={() => handleRunAgent(a.id)}
                  disabled={disabled && !["audit", "evaluate", "forecast", "recommend", "escalate", "report"].includes(a.id)}
                  className="text-left p-3 rounded border border-border hover:bg-muted/40 hover:border-primary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                  aria-label={`Executar agente ${a.name}`}
                >
                  <div className="flex items-start gap-2">
                    {isPending ? (
                      <Loader2 className={`w-4 h-4 ${a.color} animate-spin shrink-0 mt-0.5`} />
                    ) : (
                      <Icon className={`w-4 h-4 ${a.color} shrink-0 mt-0.5`} aria-hidden="true" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-foreground">{a.name}</div>
                      <p className="text-[10px] text-muted-foreground line-clamp-2">{a.desc}</p>
                      {a.needs && (
                        <Badge variant="outline" className="text-[9px] mt-1">
                          requer {a.needs}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AgentResultDrawer open={drawerOpen} onOpenChange={setDrawerOpen} result={agentResult} />
    </div>
  );
}

// ── CreateRuleDialog ──
function CreateRuleDialog({
  open, onOpenChange, orgId, onCreate, isPending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  orgId?: string;
  onCreate: (payload: {
    org_id: string; name: string; description?: string;
    metric: string; condition: string; threshold: number;
    severity: string; cooldown_minutes: number;
  }) => void;
  isPending: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [metric, setMetric] = useState(METRIC_OPTIONS[0].value);
  const [condition, setCondition] = useState(">");
  const [threshold, setThreshold] = useState("0");
  const [severity, setSeverity] = useState<string>("medium");
  const [cooldown, setCooldown] = useState("60");

  const submit = () => {
    if (!orgId || !name) return;
    onCreate({
      org_id: orgId,
      name, description: description || undefined,
      metric, condition, threshold: Number(threshold) || 0,
      severity, cooldown_minutes: Number(cooldown) || 60,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={!orgId}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nova
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar regra Sentinel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="rname">Nome</Label>
            <Input id="rname" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Alta taxa de erro" />
          </div>
          <div>
            <Label htmlFor="rdesc">Descrição</Label>
            <Textarea id="rdesc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Métrica</Label>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METRIC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severidade</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Condição</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rthr">Limite</Label>
              <Input id="rthr" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="rcd">Cooldown (min)</Label>
              <Input id="rcd" type="number" value={cooldown} onChange={(e) => setCooldown(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={isPending || !name}>
            {isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Criar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
